import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { bookingsAPI } from '../../../services/api';
import { getAllBuildings } from '../../../models/buildingModel';
import { getAllApartments } from '../../../models/apartmentModel';
import ApartmentSelectDropdown from './ApartmentSelectDropdown';
import { ArrowLeft, Save, Building2, User, Calendar, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BookingForm = () => {
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [checkingConflicts, setCheckingConflicts] = useState(false);
    const [conflicts, setConflicts] = useState([]);

    const [buildings, setBuildings] = useState([]);
    const [apartments, setApartments] = useState([]);

    const [formData, setFormData] = useState({
        buildingId: '',
        apartmentId: '',
        prospectName: '',
        prospectPhone: '',
        prospectEmail: '',
        moveInDate: '',
        moveOutDate: '',
        holdHours: 24,
        notes: ''
    });

    useEffect(() => {
        loadInitialData();
    }, [id]);

    const loadInitialData = async () => {
        try {
            setFetchingData(true);
            const buildRes = await getAllBuildings();
            // Expected response from getAllBuildings is either an array or an object with buildings list
            const buildingsList = Array.isArray(buildRes) ? buildRes : (buildRes.buildings || buildRes.data || []);
            setBuildings(buildingsList);

            if (isEditing) {
                const bRes = await bookingsAPI.getById(id);
                const booking = bRes.booking;

                await handleBuildingChange(booking.buildingId);

                setFormData({
                    buildingId: booking.buildingId || '',
                    apartmentId: booking.apartmentId || '',
                    prospectName: booking.prospectName || '',
                    prospectPhone: booking.prospectPhone || '',
                    prospectEmail: booking.prospectEmail || '',
                    moveInDate: booking.moveInDate ? booking.moveInDate.split('T')[0] : '',
                    moveOutDate: booking.moveOutDate ? booking.moveOutDate.split('T')[0] : '',
                    holdHours: booking.holdHours || 24,
                    notes: booking.notes || ''
                });
            }
        } catch (error) {
            console.error('Initial load error:', error);
            toast.error(t('error_loading_data'));
        } finally {
            setFetchingData(false);
        }
    };

    const handleDateChange = (name, date) => {
        if (!date) {
            setFormData(prev => ({ ...prev, [name]: '' }));
            return;
        }

        // Format as YYYY-MM-DD using local time to avoid off-by-one errors
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        setFormData(prev => ({ ...prev, [name]: formattedDate }));
    };

    const handleBuildingChange = async (bId) => {
        try {
            setFormData(prev => ({ ...prev, buildingId: bId, apartmentId: '' }));
            if (!bId) {
                setApartments([]);
                return;
            }
            const res = await getAllApartments({ buildingId: bId, forBooking: true });
            const aptList = Array.isArray(res) ? res : (res.apartments || res.data || []);
            setApartments(aptList);
        } catch (error) {
            console.error('Fetch apartments error', error);
        }
    };

    const handleCheckConflicts = async () => {
        if (!formData.apartmentId || !formData.moveInDate) {
            toast.error(t('select_apartment_move_in_date_error'));
            return;
        }

        try {
            setCheckingConflicts(true);
            const res = await bookingsAPI.checkConflicts({
                apartmentId: formData.apartmentId,
                moveInDate: formData.moveInDate,
                moveOutDate: formData.moveOutDate || null,
                excludeBookingId: isEditing ? id : null
            });

            setConflicts(res.conflicts || []);
            if (res.hasConflicts) {
                toast.error(t('conflict_detected'));
            } else {
                toast.success(t('apartment_available'));
            }
        } catch (error) {
            console.error('Check conflict error:', error);
            toast.error(t('error_checking_availability'));
        } finally {
            setCheckingConflicts(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (conflicts.length > 0) {
            if (!window.confirm(t('confirm_conflict_booking'))) {
                return;
            }
        }

        try {
            setLoading(true);
            const payload = { ...formData };
            if (!payload.moveOutDate) delete payload.moveOutDate;

            if (isEditing) {
                await bookingsAPI.update(id, payload);
                toast.success(t('booking_updated'));
            } else {
                await bookingsAPI.create(payload);
                toast.success(t('booking_confirmed'));
            }
            navigate('/admin/bookings');
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error.response?.data?.message || t('error_saving_booking'));
        } finally {
            setLoading(false);
        }
    };

    if (fetchingData) {
        return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;
    }

    const inputClasses = `w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:ring-red-500 outline-none transition-colors ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`;
    const labelClasses = `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`;

    return (
        <div dir={isRTL ? 'rtl' : 'ltr'} className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link to="/admin/bookings" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}>
                        <ArrowLeft size={20} />
                    </Link>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {isEditing ? t('edit_booking') : t('new_booking_hold')}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Property Details */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <h2 className={`text-lg font-semibold flex items-center mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <Building2 className={`mr-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={20} />
                        {t('property_information')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={labelClasses}>{t('building')}</label>
                            <select
                                required
                                value={formData.buildingId}
                                onChange={(e) => handleBuildingChange(e.target.value)}
                                className={inputClasses}
                            >
                                <option value="">{t('select_building')}</option>
                                {buildings.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClasses}>{t('apartment_element')}</label>
                            <ApartmentSelectDropdown
                                apartments={apartments}
                                value={formData.apartmentId}
                                onChange={(val) => setFormData({ ...formData, apartmentId: val })}
                                disabled={!formData.buildingId || apartments.length === 0}
                                t={t}
                                isDarkMode={isDarkMode}
                            />
                        </div>
                    </div>
                </div>

                {/* Prospect Details */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <h2 className={`text-lg font-semibold flex items-center mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <User className={`mr-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={20} />
                        {t('prospect_details')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={labelClasses}>{t('prospect_name')}</label>
                            <input
                                type="text"
                                required
                                value={formData.prospectName}
                                onChange={(e) => setFormData({ ...formData, prospectName: e.target.value })}
                                className={inputClasses}
                                placeholder="John Doe"
                            />
                        </div>
                        <div>
                            <label className={labelClasses}>{t('prospect_phone')}</label>
                            <input
                                type="tel"
                                required
                                value={formData.prospectPhone}
                                onChange={(e) => setFormData({ ...formData, prospectPhone: e.target.value })}
                                className={inputClasses}
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelClasses}>{t('prospect_email')} ({t('optional')})</label>
                            <input
                                type="email"
                                value={formData.prospectEmail}
                                onChange={(e) => setFormData({ ...formData, prospectEmail: e.target.value })}
                                className={inputClasses}
                                placeholder="john@example.com"
                            />
                        </div>
                    </div>
                </div>

                {/* Booking Dates */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <div className="flex justify-between items-center mb-6">
                        <h2 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <Calendar className={`mr-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={20} />
                            {t('requested_dates')}
                        </h2>
                    </div>

                    {conflicts.length > 0 && (
                        <div className={`mb-6 p-4 rounded-lg flex items-start ${isDarkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                            <AlertTriangle className={`mr-3 shrink-0 ${isDarkMode ? 'text-red-500' : 'text-red-600'}`} size={20} />
                            <div>
                                <h3 className={`font-medium text-sm ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>Conflict Detected</h3>
                                <p className={`text-sm mt-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                    This apartment already has {conflicts.length} active booking(s) for these dates.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className={labelClasses}>{t('move_in_date')}</label>
                            <div className="relative">
                                <DatePicker
                                    selected={formData.moveInDate ? new Date(formData.moveInDate) : null}
                                    onChange={(date) => handleDateChange('moveInDate', date)}
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="mm/dd/yyyy"
                                    required
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    portalId="root-portal"
                                />
                                <Calendar className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} pointer-events-none`} size={18} />
                            </div>
                        </div>
                        <div>
                            <label className={labelClasses}>{t('move_out_date')} ({t('optional')})</label>
                            <div className="relative">
                                <DatePicker
                                    selected={formData.moveOutDate ? new Date(formData.moveOutDate) : null}
                                    onChange={(date) => handleDateChange('moveOutDate', date)}
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="mm/dd/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    portalId="root-portal"
                                />
                                <Calendar className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} pointer-events-none`} size={18} />
                            </div>
                        </div>
                        {(!isEditing || status === 'pending') && (
                            <div>
                                <label className={labelClasses}>{t('hold_duration')} ({t('hours')})</label>
                                <div className="relative">
                                    <Clock className={`absolute start-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`} size={18} />
                                    <select
                                        value={formData.holdHours}
                                        onChange={(e) => setFormData({ ...formData, holdHours: parseInt(e.target.value) })}
                                        className={`${inputClasses} ps-10`}
                                    >
                                        <option value="12">12 {t('hours')}</option>
                                        <option value="24">24 {t('hours')}</option>
                                        <option value="48">48 {t('hours')}</option>
                                        <option value="72">72 {t('hours')}</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="flex items-end mb-1">
                            <button
                                type="button"
                                onClick={handleCheckConflicts}
                                disabled={checkingConflicts || !formData.apartmentId || !formData.moveInDate}
                                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors ${checkingConflicts || !formData.apartmentId || !formData.moveInDate ? 'opacity-50 cursor-not-allowed' : ''} ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
                            >
                                {checkingConflicts ? t('checking_availability') : t('check_availability')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Additional Details */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <label className={labelClasses}>{t('internal_notes')}</label>
                    <textarea
                        rows="3"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className={inputClasses}
                        placeholder="Add any special requirements or notes here..."
                    ></textarea>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/bookings')}
                        className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? 'text-zinc-300 bg-zinc-800 hover:bg-zinc-700' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`px-6 py-2.5 rounded-lg font-medium flex items-center transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''} bg-red-600 hover:bg-red-700 text-white`}
                    >
                        {loading ? t('saving') : (
                            <>
                                <Save size={18} className="mr-2" />
                                {isEditing ? t('save_changes') : t('create_hold')}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BookingForm;
